import { CheckCircleFilled, CloseCircleFilled } from '@ant-design/icons';
import { Spin } from 'antd';
import { observer } from 'mobx-react';
import React from 'react';
import { useContestStore } from '../../../contexts/mobx';
import { Contest, ContestPublicationRule } from '../../../stores/contest';
import { describeRule, RuleTab } from './rules';
import style from './style.module.scss';

interface ChecklistProps {
    contest: Contest;
    /** A tab holds unsaved changes, which the checklist does not see. */
    hasUnsavedChanges: boolean;
    onOpenTab: (tab: RuleTab) => void;
}

/** A policy by its title, as the Policies tab shows it. */
function policyNameIn(contest: Contest) {
    return (policyId: number) => {
        const policy = contest.policies.find((each) => each.id === policyId);
        const title =
            policy?.translations.find((each) => each.lang === 'en')?.title ??
            policy?.translations[0]?.title;
        return title ?? `#${policyId}`;
    };
}

const RuleList = ({
    rules,
    contest,
    onOpenTab,
}: {
    rules: ContestPublicationRule[];
    contest: Contest;
    onOpenTab: (tab: RuleTab) => void;
}) => (
    <ul className={style.rules}>
        {rules.map((rule) => {
            const { title, details, tab } = describeRule(
                rule,
                policyNameIn(contest)
            );
            const content = (
                <>
                    {rule.ok ? (
                        <CheckCircleFilled
                            className={style.icon}
                            aria-label="Kept"
                        />
                    ) : (
                        <CloseCircleFilled
                            className={style.icon}
                            aria-label="Missing"
                        />
                    )}
                    <span className={style.text}>
                        <span>{title}</span>
                        {details.map((detail) => (
                            <span key={detail} className={style.detail}>
                                {detail}
                            </span>
                        ))}
                    </span>
                </>
            );
            return (
                <li
                    key={rule.rule}
                    className={rule.ok ? style.kept : style.failed}
                >
                    {rule.ok ? (
                        <div className={style.rule}>{content}</div>
                    ) : (
                        <button
                            type="button"
                            className={style.rule}
                            onClick={() => onOpenTab(tab)}
                        >
                            {content}
                        </button>
                    )}
                </li>
            );
        })}
    </ul>
);

/**
 * The publication checklist, run on the contest as saved. A draft shows every
 * rule, kept or failed; a failed one opens the tab that fixes it. A
 * published contest cannot be saved into breaking a rule, so it shows only
 * what a refused save would have broken.
 */
export const Checklist = observer(
    ({ contest, hasUnsavedChanges, onOpenTab }: ChecklistProps) => {
        const store = useContestStore();
        const refused = store.refusedRules;

        if (contest.isActive) {
            return refused ? (
                <div className={style.checklist}>
                    <p className={style.refusal} role="alert">
                        The last save was refused and nothing was saved: the
                        contest is published, and these rules would no longer
                        hold.
                    </p>
                    <RuleList
                        rules={refused}
                        contest={contest}
                        onOpenTab={onOpenTab}
                    />
                </div>
            ) : (
                <p className={style.note}>
                    Published. A save that would break a rule of the checklist
                    is refused.
                </p>
            );
        }

        const publication =
            store.publication?.contestId === contest.id
                ? store.publication
                : null;
        // A failed read leaves the last list out of date, so it is not shown.
        if (store.publicationFetchFailed) {
            return (
                <p className={style.note}>The checklist could not be read.</p>
            );
        }
        if (!publication) {
            return (
                <div className={style.loading}>
                    <Spin />
                </div>
            );
        }

        return (
            <div className={style.checklist}>
                {hasUnsavedChanges && (
                    <p className={style.note}>Save to update the checklist.</p>
                )}
                <RuleList
                    rules={publication.rules}
                    contest={contest}
                    onOpenTab={onOpenTab}
                />
            </div>
        );
    }
);
